import { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AdyenCheckout, Dropin } from '@adyen/adyen-web/auto'
import '@adyen/adyen-web/styles/adyen.css'
import { ApiPlan } from 'builder/modules/billing/types'
import { actions as billingActions, selectors } from 'builder/modules/billing/billingModule'
import { Spinner } from 'builder/components/Spinner'
import Icon24 from 'builder/components/Icon'
import { useConfig } from 'builder/hooks/useConfig'
import {
  PaymentContainer,
  PaymentHeader,
  BackButton,
  TotalSection,
  TotalLabel,
  TotalAmount,
  PaymentMethodsContainer,
  DropinContainer,
} from './paymentStyles'

interface PaymentFormProps {
  selectedPlan: string
  planAmount: string
  planData: ApiPlan
  onBack: () => void
  onPaymentSuccess: (piToken: string) => void
  onPaymentError: (error: unknown) => void
}

export const PaymentForm = ({
  selectedPlan,
  planAmount,
  planData,
  onBack,
  onPaymentSuccess,
  onPaymentError,
}: PaymentFormProps) => {
  const dispatch = useDispatch()
  const [dropinInstance, setDropinInstance] = useState<{ submit: () => void } | null>(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('')
  const dropinRef = useRef<HTMLDivElement>(null)
  const config = useConfig()
  const paymentMethods = useSelector(selectors.paymentMethods)
  const paymentMethodsLoading = useSelector(selectors.paymentMethodsLoading)
  const adyenConfig = useSelector(selectors.adyenConfig)
  const adyenConfigLoading = useSelector(selectors.adyenConfigLoading)
  const isLoading = paymentMethodsLoading || adyenConfigLoading

  useEffect(() => {
    dispatch(billingActions.fetchAdyenConfig())
  }, [dispatch])

  useEffect(() => {
    if (selectedPlan) {
      dispatch(billingActions.fetchPaymentMethods({ plan: selectedPlan }))
    }
  }, [dispatch, selectedPlan])

  const getAvsStatus = (): 'hide' | 'partial' | 'full' => {
    return config?.features?.avsStatus || 'hide'
  }

  const getPaymentAmountInCents = (): number => {
    return planData.amount || planData.recurringAmount
  }

  const createPayment = async (paymentData: Record<string, unknown>) => {
    const response = await fetch('/app/billing/charge_bee/create_payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json', // eslint-disable-line @typescript-eslint/naming-convention
      },
      body: JSON.stringify({
        ...paymentData,
        plan: selectedPlan,
        amount: (getPaymentAmountInCents() / 100).toString(),
        protocol: window.location.protocol,
        host: window.location.host,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to create payment')
    }

    return response.json()
  }

  const initializeAdyen = useCallback(async () => {
    if (!paymentMethods || !adyenConfig || isLoading) {
      return
    }

    try {
      const paymentMethodsData = { ...paymentMethods }
      if (paymentMethodsData.amount && paymentMethodsData.amount.value === 0) {
        paymentMethodsData.amount.value = getPaymentAmountInCents()
      }

      const { client_key: clientKey, environment } = adyenConfig

      const handleServerResponse = (
        response: Record<string, unknown>,
        component: unknown,
        actions: unknown,
      ) => {
        const { resultCode, action, merchantReference } = response

        if (action) {
          // eslint-disable-next-line @typescript-eslint/no-extra-semi
          ;(component as { handleAction: (action: unknown) => void }).handleAction(action)
        } else {
          switch (resultCode) {
            case 'Authorised':
              // eslint-disable-next-line @typescript-eslint/no-extra-semi
              ;(actions as { resolve: (result: unknown) => void }).resolve({ resultCode, action })
              onPaymentSuccess(merchantReference as string)
              break
            case 'Pending':
            case 'Received':
              // eslint-disable-next-line @typescript-eslint/no-extra-semi
              ;(actions as { resolve: (result: unknown) => void }).resolve({ resultCode, action })
              onPaymentSuccess(merchantReference as string)
              break
            default:
              // eslint-disable-next-line @typescript-eslint/no-extra-semi
              ;(actions as { reject: () => void }).reject()
              onPaymentError(response)
              break
          }
        }
      }

      const checkoutInstance = await AdyenCheckout({
        clientKey,
        environment,
        locale: 'en-US',
        amount: paymentMethodsData.amount,
        countryCode: 'US',
        translations: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'en-US': {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'payButton.saveDetails': 'Pay',
          },
        },
        paymentMethodsResponse: paymentMethodsData.payment_methods,

        onSubmit: async (state, component, actions) => {
          try {
            // eslint-disable-next-line @typescript-eslint/no-extra-semi
            const response = await createPayment(state.data as unknown as Record<string, unknown>)
            ;(
              component as unknown as { props: { lastMerchantReference: string } }
            ).props.lastMerchantReference = (
              response as { merchantReference: string }
            ).merchantReference
            handleServerResponse(response, component, actions)
          } catch (error) {
            onPaymentError(error)
          }
        },

        onAdditionalDetails: async (state, component, actions) => {
          try {
            const data = {
              ...state.data,
              merchant_reference: (
                component as unknown as { props: { lastMerchantReference: string } }
              ).props.lastMerchantReference,
            }

            const response = await fetch('/app/billing/charge_bee/additional_payment_details', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json', // eslint-disable-line @typescript-eslint/naming-convention
              },
              body: JSON.stringify(data),
            })

            if (!response.ok) {
              throw new Error(`Additional payment details failed: ${response.status}`)
            }

            const additionalDetailsResponse = await response.json()

            handleServerResponse(additionalDetailsResponse, component, actions)
          } catch (error) {
            onPaymentError(error)
          }
        },

        onError: (error: { name: string; message: unknown }) => {
          // Google Pay close dialog
          if (error.name === 'CANCEL' && error.message?.toString() === '[object Object]') {
            return
          }
          // ApplePay close dialog
          if (error.name === 'CANCEL' && error.message === 'ApplePay UI dismissed') {
            return
          }
          // Paypal close dialog
          if (error.name === 'ERROR' && error.message === 'Error: Detected popup close') {
            return
          }
          if (
            error.name === 'CANCEL' &&
            error.message === 'AbortError: User closed the Payment Request UI'
          ) {
            return
          }
          if (error.name === 'CANCEL' && error.message?.toString().trim() === '') {
            return
          }

          onPaymentError(error)
        },
      })

      const dropinInstance = new Dropin(checkoutInstance, {
        showRadioButton: true,
        showStoredPaymentMethods: false,
        disableFinalAnimation: true,
        openFirstPaymentMethod: false,
        onSelect: (component: { props?: { type?: string } }) => {
          setSelectedPaymentMethod(component?.props?.type || '')
        },
        paymentMethodsConfiguration: {
          card: {
            hasHolderName: true,
            holderNameRequired: true,
            enableStoreDetails: false,
            showPayButton: false,
            placeholders: {
              cardNumber: '1234 5678 9012 3456',
              expiryDate: 'MM/YY',
              securityCodeThreeDigits: '123',
              holderName: 'John Smith',
            },
            billingAddressRequired: getAvsStatus() !== 'hide',
            billingAddressMode:
              getAvsStatus() === 'hide' ? 'none' : (getAvsStatus() as 'partial' | 'full'),
            data: {
              billingAddress: {
                country: config?.features?.geoipCode || 'US',
              },
            },
          },
          paywithgoogle: {
            buttonType: 'plain',
            ...(paymentMethodsData.payment_methods?.paymentMethods?.find(
              (pm: { type: string }) => pm.type === 'googlepay',
            )?.configuration || {}),
          },
          applepay: {
            buttonType: 'plain',
            buttonColor: 'black',
            showPayButton: true,
            ...(paymentMethodsData.payment_methods?.paymentMethods?.find(
              (pm: { type: string }) => pm.type === 'applepay',
            )?.configuration || {
              merchantId: '000000000208819',
              merchantName: 'TalentIncECOM',
            }),
          },
          paypal: {
            style: {
              height: 48,
            },
            ...(paymentMethodsData.payment_methods?.paymentMethods?.find(
              (pm: { type: string }) => pm.type === 'paypal',
            )?.configuration || {}),
          },
        },
      })

      setDropinInstance(dropinInstance)

      const mountDropin = () => {
        if (dropinRef.current) {
          try {
            dropinInstance.mount(dropinRef.current)
            return true
          } catch (mountError) {
            onPaymentError(mountError)
            return false
          }
        }
        return false
      }

      if (!mountDropin()) {
        setTimeout(() => {
          if (!mountDropin()) {
            onPaymentError(new Error('Failed to mount payment form - container not found'))
          }
        }, 100)
      }
    } catch (error) {
      onPaymentError(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentMethods,
    adyenConfig,
    isLoading,
    selectedPlan,
    planAmount,
    onPaymentSuccess,
    onPaymentError,
  ])

  useEffect(() => {
    initializeAdyen()
  }, [initializeAdyen])

  useEffect(() => {
    if (!isLoading && dropinInstance && selectedPaymentMethod === 'scheme') {
      const creditCardMethod = document.querySelector(
        '.adyen-checkout__payment-method--scheme, .adyen-checkout__payment-method[data-type="scheme"], .adyen-checkout__payment-method--card',
      )
      let existingButton = document.getElementById('injected-pay-button')

      if (creditCardMethod && !existingButton) {
        const button = document.createElement('button')
        button.id = 'injected-pay-button'
        button.textContent = `Pay ${planAmount}`
        button.style.cssText = `
          width: 100%;
          height: 48px;
          background: #1A91F0;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        `

        button.addEventListener('click', () => {
          try {
            dropinInstance.submit()
          } catch (error) {
            onPaymentError(error)
          }
        })

        button.addEventListener('mouseenter', () => {
          button.style.background = '#1170CD'
        })

        button.addEventListener('mouseleave', () => {
          button.style.background = '#1A91F0'
        })

        creditCardMethod.appendChild(button)
      }
    } else {
      const existingButton = document.getElementById('injected-pay-button')
      if (existingButton) {
        existingButton.remove()
      }
    }
  }, [isLoading, dropinInstance, selectedPaymentMethod, planAmount, onPaymentError])

  return (
    <PaymentContainer>
      <PaymentHeader>
        <BackButton onClick={onBack}>
          <Icon24.ChevronLeft />
          Back
        </BackButton>
      </PaymentHeader>

      <TotalSection>
        <TotalLabel>Total Due today</TotalLabel>
        <TotalAmount>{planAmount}</TotalAmount>
      </TotalSection>

      <PaymentMethodsContainer $isLoading={isLoading}>
        {isLoading && <Spinner $width="64px" $height="64px" />}
        <DropinContainer
          ref={dropinRef}
          id="upgrade-modal-dropin-container"
          style={{ display: isLoading ? 'none' : 'block' }}
        />
      </PaymentMethodsContainer>
    </PaymentContainer>
  )
}
